import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Mutation } from "src/types/mutation";
import { Query } from "src/types/query";
import useAuth from "src/hooks/useAuth";
import axios from "src/utils/axios";
import {
    BulkOperationTargetEnum,
    type BulkOperation,
    type BulkOperationOperationType,
    type BulkOperationTarget,
} from "./type";

interface CreateBulkOperationMetadata {
    posIds?: number[];
}

export interface CreateBulkOperationPayload {
    target: BulkOperationTarget;
    operationType: BulkOperationOperationType;
    operationIdList: number[];
    metadata?: CreateBulkOperationMetadata;
}

export function useCreateBulkOperation() {
    const queryClient = useQueryClient();
    const { user } = useAuth();

    return useMutation({
        mutationKey: [Mutation.CreateBulkOperation, user.id],
        mutationFn: async (payload: CreateBulkOperationPayload) => {
            const organizationId = user.organization.id;
            const userId = user.id;

            const { data } = await axios.post<BulkOperation>("/bulk-operation", {
                target: payload.target,
                operationType: payload.operationType,
                organizationId,
                userId,
                operationIdList: payload.operationIdList,
                metadata: payload.metadata,
            });

            return data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: [Query.UnseenBulkOperations] });

            if (variables.target === BulkOperationTargetEnum.Offer) {
                queryClient.invalidateQueries({ queryKey: [Query.OfferManagement] });
                return;
            }

            queryClient.invalidateQueries({ queryKey: [Query.SponsoredAdManagement] });
        },
    });
}

export function useMarkBulkOperationSeen() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: number) => {
            await axios.patch(`/bulk-operation/${id}/seen`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [Query.UnseenBulkOperations] });
        },
    });
}
